package com.evoapps.evoshift.navigation

import com.evoapps.evoshift.Route
import com.evoapps.evoshift.voice.VoiceAction
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlin.reflect.KClass

/**
 * Links that open a screen (as EVoLabels' DeepLinks): Android App Links, iOS URLs and the web
 * app's own address, one mapping for every platform. A link waits here until the person can see
 * it ([linkPlacement]), so an invitation opened while signed out survives signing up or in. The
 * NavHost has no deep links of its own: it would open them before anyone is signed in.
 */
object DeepLinks {
    private val _pending = MutableStateFlow<Route?>(null)
    val pending: StateFlow<Route?> = _pending.asStateFlow()

    /**
     * The screen for [url], or null: `https://evoshift.app/join?code=…` (also the web app served
     * at /join) opens the invitation. Other addresses (/web, `?emulator`) open no screen.
     */
    fun routeFor(url: String): Route? {
        val withoutFragment = url.substringBefore('#')
        val address = withoutFragment.substringBefore('?')
        val path = "/" + address.substringAfter("://", address).substringAfter('/', "").trimEnd('/')
        val query = withoutFragment.substringAfter('?', "")
        return when {
            path == "/join" || path.startsWith("/join/") ->
                queryValue(query, "code")?.let { Route.JoinTeam(it) }
            path.startsWith(NOTIFY_PATH) -> notificationRoute(path.removePrefix(NOTIFY_PATH))
            // SH-150: evoshift://app/voice/<action> (Siri, Google Assistant, Android shortcuts).
            path.startsWith(VOICE_PATH) ->
                VoiceAction.fromPath(path.removePrefix(VOICE_PATH))?.let { Route.VoiceAction(it.path, ++voiceRequests) }
            else -> null
        }
    }

    /**
     * A tap on a push or its action (SH-99, Android push/PushNotifications.kt): app-internal
     * links, never published, so they use the app's own scheme.
     */
    private const val NOTIFY_PATH = "/notify/"
    private const val VOICE_PATH = "/voice/"
    private var voiceRequests = 0L
    private const val NOTIFY = "evoshift://app$NOTIFY_PATH"
    const val NOTIFY_LIST = "${NOTIFY}list"
    const val NOTIFY_SCHEDULE = "${NOTIFY}schedule"
    const val NOTIFY_MARKETPLACE = "${NOTIFY}marketplace"
    const val NOTIFY_ABSENCES = "${NOTIFY}absences"
    const val NOTIFY_AVAILABILITY = "${NOTIFY}availability"

    private fun notificationRoute(screen: String): Route? = when (screen) {
        "list" -> Route.Notifications
        "schedule" -> Route.Schedule()
        "marketplace" -> Route.Schedule(showMarketplace = true)
        "absences" -> Route.Absences
        "availability" -> Route.Availability
        else -> null
    }

    /** Opens [route] once the person can see it. */
    fun open(route: Route?) {
        if (route != null) _pending.value = route
    }

    /** Opens the screen of [url], if it has one; returns whether it had. */
    fun openUrl(url: String): Boolean = routeFor(url)?.also(::open) != null

    /** The waiting screen, removed so it opens only once. */
    fun consume(): Route? = _pending.value.also { _pending.value = null }

    private fun queryValue(query: String, key: String): String? = query.split('&')
        .firstOrNull { it.substringBefore('=') == key }
        ?.substringAfter('=', "")
        ?.let(::percentDecode)
        ?.trim()
        ?.takeIf { it.isNotBlank() }

    /** Invitation codes are plain ASCII; `%XX` beyond it is kept as it is. */
    private fun percentDecode(value: String): String = buildString {
        var i = 0
        while (i < value.length) {
            val c = value[i]
            val code = if (c == '%' && i + 2 <= value.lastIndex) value.substring(i + 1, i + 3).toIntOrNull(16) else null
            if (code != null && code < 0x80) {
                append(code.toChar())
                i += 3
            } else {
                append(if (c == '+') ' ' else c)
                i++
            }
        }
    }
}

/** Where the signed-in state stands: what the app may show. */
enum class SessionGate { SIGNED_OUT, UNVERIFIED, NEEDS_ONBOARDING, READY }

/** The first screen for [gate], before any link. */
fun startRoute(gate: SessionGate): Route = when (gate) {
    SessionGate.SIGNED_OUT, SessionGate.UNVERIFIED -> Route.Login
    SessionGate.NEEDS_ONBOARDING -> Route.Onboarding
    SessionGate.READY -> Route.Dashboard
}

/** Where a waiting link goes. */
enum class LinkPlacement {
    /** Not yet: the person signs in, verifies the address or finishes the first run first. */
    WAIT,

    /** Instead of everything else (signing in with the invitation, joining instead of onboarding). */
    ROOT,

    /** On top of the current screen. */
    ON_TOP
}

/**
 * Where [link] opens for [gate] with [current] on top.
 *
 * Signed out (or with an unverified address) every link waits on the login screen: an invitation
 * can only be read signed in (firestore.rules), and signing up or in hands it over itself
 * ([routeAfterSignIn]) - the login screen never opens links, so the two cannot race. An
 * invitation is its own way in: it replaces the first run, and otherwise opens on top.
 */
fun linkPlacement(gate: SessionGate, link: Route, current: KClass<out Route>?): LinkPlacement = when {
    current == null || current == Route.Login::class -> LinkPlacement.WAIT
    gate == SessionGate.SIGNED_OUT || gate == SessionGate.UNVERIFIED -> LinkPlacement.WAIT
    link is Route.JoinTeam ->
        if (gate == SessionGate.NEEDS_ONBOARDING || current == Route.Onboarding::class) LinkPlacement.ROOT
        else LinkPlacement.ON_TOP
    gate == SessionGate.NEEDS_ONBOARDING || current in fullScreenRoutes -> LinkPlacement.WAIT
    else -> LinkPlacement.ON_TOP
}

/** Where signing in leads: a waiting invitation, else home (which starts the first run if needed). */
fun routeAfterSignIn(link: Route?): Route = link as? Route.JoinTeam ?: Route.Dashboard
